/**
 * LeadDrawer — right-side panel (420 px on lg+), full-screen sheet below lg.
 * Dialog semantics, focus trap, Escape closes, body scroll locked.
 */
import { useRef } from 'react';
import { Link } from 'react-router';
import { Maximize2, X } from 'lucide-react';
import { useFocusTrap } from '@/lib/focus-trap';
import { LeadDetail } from './LeadDetail';

export function LeadDrawer({ leadId, onClose }: { leadId: string | null; onClose: () => void }) {
  const panelRef = useRef<HTMLDivElement>(null);
  useFocusTrap(panelRef, { active: !!leadId, onEscape: onClose, initialFocus: '[data-drawer-close]' });

  if (!leadId) return null;

  return (
    <div className="fixed inset-0 z-[60]" data-testid="lead-drawer">
      <div className="absolute inset-0 bg-navy-dark/40 lg:bg-navy-dark/20" onClick={onClose} aria-hidden />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="lead-drawer-title"
        aria-label="Candidate"
        tabIndex={-1}
        className="absolute inset-0 flex flex-col bg-white shadow-2xl outline-none overscroll-contain lg:inset-y-0 lg:left-auto lg:right-0 lg:w-[420px] lg:border-l lg:border-gray-200"
      >
        <div className="flex items-center justify-end gap-1 border-b border-gray-100 px-2 py-1.5">
          <Link
            to={`/leads/${leadId}`}
            className="inline-flex min-h-10 items-center gap-1.5 rounded-md px-2.5 text-[13px] font-medium text-muted hover:bg-page hover:text-gray-900"
          >
            <Maximize2 size={14} strokeWidth={2} aria-hidden /> Open full page
          </Link>
          <button
            type="button"
            data-drawer-close
            onClick={onClose}
            aria-label="Close candidate"
            className="flex h-10 w-10 items-center justify-center rounded-md text-gray-500 hover:bg-page hover:text-gray-900"
          >
            <X size={18} strokeWidth={2} aria-hidden />
          </button>
        </div>
        <div className="min-h-0 flex-1">
          <LeadDetail leadId={leadId} titleId="lead-drawer-title" />
        </div>
      </div>
    </div>
  );
}
